'use client'

import React, { Suspense } from 'react'

import { useQuery, useSuspenseQueries } from '@tanstack/react-query'
import { User } from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { useRecipientSectionQueries } from '@/hooks/usaspending'

import { QueryErrorBoundary } from '../../DataErrorBoundary'
import { Currency, NumberDisplay } from '../../Formatters'
import { SectionCard, SectionSkeleton, useDetailFilter } from '../../shared'
import { CardGridUnavailable } from '../../shared/ServiceUnavailable'
import { useRecipientDetail } from '../context'

/**
 * RecipientDetail.Officers
 *
 * Displays executive officers with compensation data (if available).
 * Falls back to unattributed award stats if no officer data exists.
 */
export function OfficersSection() {
  const { recipientId } = useRecipientDetail()

  return (
    <SectionCard
      id="officers"
      title="Executive Officers"
      fullWidth
      animationDelay={400}
    >
      <QueryErrorBoundary
        title="Failed to load officer data"
        serviceUnavailableFallback={<CardGridUnavailable />}
      >
        <Suspense fallback={<SectionSkeleton height={120} bars={2} />}>
          <OfficersContent id={recipientId} />
        </Suspense>
      </QueryErrorBoundary>
    </SectionCard>
  )
}

function OfficersContent({ id }: { id: string }) {
  const queries = useRecipientSectionQueries()
  const { state } = useDetailFilter()
  const fiscalYear = state.fiscalYear === 'all' ? undefined : state.fiscalYear
  const [{ data: profile }, { data: stats }] = useSuspenseQueries({
    queries: [queries.profile(id), queries.stats(id, fiscalYear)],
  })

  const isDeferred =
    profile.deferredFields?.includes('officers') &&
    profile.officers === undefined

  if (isDeferred) {
    return <DeferredOfficers id={id} stats={stats} />
  }

  const officers = profile.officers || []

  if (officers.length === 0) {
    return <UnattributedStats stats={stats} />
  }

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {officers.slice(0, 5).map((officer, index) => (
        <OfficerCard key={officer.fullName} officer={officer} index={index} />
      ))}
    </div>
  )
}

function DeferredOfficers({
  id,
  stats,
}: {
  id: string
  stats: { totalPrimeAwards: number; totalObligatedAmount: number }
}) {
  const queries = useRecipientSectionQueries()
  const { data: officers, isLoading } = useQuery(queries.officers(id, true))

  if (isLoading) {
    return <SectionSkeleton height={120} bars={2} />
  }

  if (!officers || officers.length === 0) {
    return <UnattributedStats stats={stats} />
  }

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {officers.slice(0, 5).map((officer, index) => (
        <OfficerCard key={officer.fullName} officer={officer} index={index} />
      ))}
    </div>
  )
}

interface OfficerCardProps {
  officer: {
    fullName: string
    compensation?: number
  }
  index: number
}

function OfficerCard({ officer, index }: OfficerCardProps) {
  // Generate initials from name
  const initials = officer.fullName
    .split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  return (
    <div
      className={twMerge(
        'flex items-center gap-3 rounded-lg border border-primary-200 bg-primary-50/50 p-3',
        'dark:border-primary-700 dark:bg-primary-800/30',
        'transition-colors hover:bg-primary-100 dark:hover:bg-primary-800/50',
      )}
      style={{ animationDelay: `${400 + index * 50}ms` }}
    >
      {/* Avatar */}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold text-white">
        {initials}
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-primary-900 dark:text-primary-100">
          {officer.fullName}
        </div>
        {officer.compensation ? (
          <div className="text-xs tabular-nums text-primary-500 dark:text-primary-400">
            <Currency value={officer.compensation} />
          </div>
        ) : (
          <div className="text-xs text-primary-400 dark:text-primary-500">
            Compensation not disclosed
          </div>
        )}
      </div>
    </div>
  )
}

interface UnattributedStatsProps {
  stats: {
    totalPrimeAwards: number
    totalObligatedAmount: number
  }
}

function UnattributedStats({ stats }: UnattributedStatsProps) {
  const avgAwardSize =
    stats.totalPrimeAwards > 0
      ? stats.totalObligatedAmount / stats.totalPrimeAwards
      : 0

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-primary-500 dark:text-primary-400">
        <User className="h-4 w-4" />
        <span>
          Executive compensation data not disclosed for this recipient.
        </span>
      </div>

      <div className="border-t border-primary-200 pt-4 dark:border-primary-700">
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
          Award Activity (No Officer Attribution)
        </h4>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-semibold tabular-nums text-primary-900 dark:text-primary-100">
              <NumberDisplay value={stats.totalPrimeAwards} />
            </div>
            <div className="text-xs text-primary-500 dark:text-primary-400">
              Awards
            </div>
          </div>
          <div>
            <div className="text-2xl font-semibold tabular-nums text-primary-900 dark:text-primary-100">
              <Currency value={stats.totalObligatedAmount} compact />
            </div>
            <div className="text-xs text-primary-500 dark:text-primary-400">
              Total Obligated
            </div>
          </div>
          <div>
            <div className="text-2xl font-semibold tabular-nums text-primary-900 dark:text-primary-100">
              <Currency value={avgAwardSize} compact />
            </div>
            <div className="text-xs text-primary-500 dark:text-primary-400">
              Avg Award
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
